import { useState } from 'react';
import {
  canFinalise,
  createFinalisedVersion,
  FINALISED_VERSION_FORMAT_GUIDANCE,
} from '@suvoda-uat/domain';
import { Button, TextInput } from '@suvoda-uat/ui-suvoda';
import type { FinaliseTrialResult, Trial } from '../lib/api-client.js';
import { canApprove } from '../lib/auth.js';
import { useFinaliseTrial } from '../lib/queries.js';

export type FinaliseControlProps = {
  readonly trial: Trial;
};

const finalisedLabel = (trial: Trial): string | null =>
  trial.status === 'finalised' && trial.latestFinalisedVersion !== undefined
    ? `Finalised ${trial.latestFinalisedVersion.version}`
    : null;

const progressOf = (trial: Trial) => trial.reviewProgress ?? { ready: 0, total: 0 };

const outstandingCount = (trial: Trial): number => {
  const progress = progressOf(trial);
  return Math.max(0, progress.total - progress.ready);
};

const rejectionMessage = (
  result: Extract<FinaliseTrialResult['result'], { rejected: unknown }>,
) => {
  const rejection = result.rejected;
  if (rejection.reason === 'not_ready') {
    const outstanding = rejection.outstanding
      ? rejection.outstanding.incompleteCount + rejection.outstanding.undecidedCount
      : 0;
    return `${outstanding} Test Cases outstanding`;
  }
  if (rejection.reason === 'version_not_greater') {
    return `Supply a version greater than ${rejection.lastFinalisedVersion}`;
  }
  return 'Trial is not in review';
};

export function FinaliseControl({ trial }: FinaliseControlProps) {
  const finalise = useFinaliseTrial(trial.id);
  const [isOpen, setIsOpen] = useState(false);
  const [version, setVersion] = useState('');
  const [message, setMessage] = useState<string | null>(finalisedLabel(trial));
  const outstanding = outstandingCount(trial);
  const isReady = trial.status === 'in_review' && outstanding === 0;

  // FR-017: only Approver+ may finalise — hide the control for everyone else.
  // The server's authorization-policy enforces this too (this is UX/defense in depth).
  if (!canApprove()) return null;

  const submit = () => {
    setMessage(null);
    let parsedVersion: ReturnType<typeof createFinalisedVersion>;
    try {
      parsedVersion = createFinalisedVersion(version);
    } catch {
      setMessage(FINALISED_VERSION_FORMAT_GUIDANCE);
      return;
    }

    if (
      !canFinalise(
        trial.status,
        {
          ready: progressOf(trial).ready,
          total: progressOf(trial).total,
          packComplete: false,
          latestFinalisedVersion:
            trial.latestFinalisedVersion === undefined
              ? null
              : createFinalisedVersion(trial.latestFinalisedVersion.version),
        },
        parsedVersion,
      )
    ) {
      setMessage('Trial is not in review');
      return;
    }

    finalise.mutate(
      { version: parsedVersion },
      {
        onSuccess: ({ result }) => {
          if ('accepted' in result) {
            const finalised = result.accepted.trial.latestFinalisedVersion;
            // F-013 (R16): surface the render-report summary with the success.
            const summary = finalised?.renderReport;
            const counts =
              summary === undefined
                ? ''
                : ` — ${summary.warningCount} warnings, ${summary.unresolvedPlaceholderCount} unresolved placeholders`;
            setMessage(`Finalised ${finalised?.version ?? parsedVersion}${counts}`);
            setIsOpen(false);
            return;
          }
          setMessage(rejectionMessage(result));
        },
      },
    );
  };

  return (
    <div className="flex flex-col items-end gap-2 text-right">
      <Button
        variant="primary"
        disabled={!isReady || finalise.isPending}
        onClick={() => setIsOpen(true)}
      >
        Finalise
      </Button>
      {!isReady && trial.status === 'in_review' ? (
        <p className="m-0 text-sm font-semibold text-text-secondary">
          {outstanding} Test Cases outstanding
        </p>
      ) : null}
      {message ? <p className="m-0 text-sm font-semibold text-text-secondary">{message}</p> : null}
      {isOpen ? (
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-left text-xs font-semibold text-text-secondary">
            <span>Version</span>
            <TextInput
              aria-label="Finalised version"
              value={version}
              onChange={(event) => setVersion(event.currentTarget.value)}
            />
          </label>
          <Button variant="outline" onClick={submit} disabled={finalise.isPending}>
            Confirm finalise
          </Button>
        </div>
      ) : null}
    </div>
  );
}
